/**
 * Theme for News24Wine (AU) site
 */
import type { Theme } from '@emotion/react';
import { globals } from 'common-ui';
import merge from 'lodash/merge';
import { icons } from 'common-font-icons';
import type { CommonThemeVars } from 'tsconfig/types.d';
import {
  buildThemeMixinsAPACLW,
  buildThemeWidgetStylesAPACLW,
  buildThemeVarsAPACLW,
} from 'dw-apac-law/theme-foundation';
import { cmsTBSStyles } from 'common-ui/src/utils/themeHelper';

type APACNewstfFontFamilies = {
  primary?: string;
  secondary?: string;
  tertiary?: string;
};

export const buildThemeVarsAPACNewstf = (vars: Pick<Theme, CommonThemeVars>, fontFamilies?: APACNewstfFontFamilies) => {
  // Core brand colours (News24Wine official brand guidelines)
  const newstfWhite = '#FFFFFF';
  const newstfSurfaceSubtle = '#F8F8F8';
  const newstfMidnight = '#0D0429';
  const newstfWellRed = '#EF1010';
  const newstfTrueBlue = '#09098D';

  // Accent colour palette (from brand accent colours guide)
  const newstfPrimarySubtle = '#FBC6C6';
  const newstfPrimaryDefault = newstfWellRed;
  const newstfPrimaryBold = '#29040D';

  const newstfSecondarySubtle = '#C2DEFF';
  const newstfSecondaryDefault = newstfTrueBlue;
  const newstfSecondaryBold = '#060213';

  const newstfTertiarySubtle = '#DCDFE2';
  const newstfTertiaryDefault = '#737A87';
  const newstfTertiaryBold = '#060213';

  // Core neutral palette (from brand neutral palette guide)
  const newstfNeutral100 = '#F5F5F5';
  const newstfNeutral200 = '#EAEBEC';
  const newstfNeutral300 = '#E0E1E2';
  const newstfNeutral400 = '#C0C3C5';
  const newstfNeutral500 = '#A1A6A9';
  const newstfNeutral600 = '#81888C';
  const newstfNeutral700 = '#606669';
  const newstfNeutral800 = '#404446';
  const newstfNeutral900 = '#202223';
  const newstfNeutral1000 = '#000000';

  // Button and link states
  const newstfButtonTextDark = '#222222';
  const newstfButtonHoverRed = '#8F0A0A';
  const newstfButtonDisabledBg = '#EDEDED';
  const newstfButtonDisabledText = '#494949';
  const newstfButtonDisabledLink = '#767676';
  const newstfUnlimitedBg = '#009F8E';
  const newstfUnlimitedHoverBg = newstfSecondaryBold;

  const lwThemeVars = buildThemeVarsAPACLW(vars, {
    oswald: fontFamilies?.primary ?? `'IBM Plex Serif', serif`,
    roboto: fontFamilies?.secondary ?? `'Inter', sans-serif`,
    noto: fontFamilies?.tertiary ?? `'Inter', sans-serif`,
  });

  return merge({}, lwThemeVars, {
    meta: {
      name: 'News24Wine AU',
    },
    icons,
    assets: {
      headerLogoImgUrl: `${globals.assetsPath}/next/images/brand-logo.svg`,
      headerLogoMobileImgUrl: `${globals.assetsPath}/next/images/brand-logo.svg`,
      drawerHeaderLogoImgUrl: `${globals.assetsPath}/next/images/brand-logo.svg`,
      drawerHeaderLogoMobileImgUrl: `${globals.assetsPath}/next/images/brand-logo.svg`,
      footerLogoImgUrl: `${globals.assetsPath}/next/images/brand-logo.svg`,
    },
    colors: {
      primary1: newstfPrimaryDefault,
      primary2: newstfSecondaryDefault,
      primary3: newstfSurfaceSubtle,
      primary4: newstfPrimaryBold,
      primary5: newstfPrimarySubtle,
      primary6: newstfPrimaryDefault,
      primary7: newstfPrimarySubtle,
      primary8: newstfNeutral1000,
      primary9: newstfPrimaryDefault,
      secondary1: newstfMidnight,
      secondary2: newstfTertiarySubtle,
      secondary3: newstfSecondarySubtle,
      secondary4: newstfTrueBlue,
      secondary5: newstfPrimarySubtle,
      secondary6: newstfPrimaryDefault,
      secondary7: newstfPrimaryBold,
      secondary8: newstfTertiaryBold,
      secondary9: newstfNeutral400,
      secondary10: newstfNeutral900,
      secondary11: newstfNeutral700,
      secondary12: newstfSecondaryDefault,
      secondary13: newstfNeutral100,
      secondary14: newstfNeutral300,
      secondary15: newstfNeutral700,
      secondary16: newstfSecondarySubtle,
      secondary17: newstfPrimaryDefault,
      textPrimaryBg: newstfPrimaryDefault,
      textSecondaryBg: newstfNeutral700,
      textUnlimitedPrice: newstfUnlimitedBg,
      textUnlimitedBg: newstfUnlimitedBg,
      textUnlimitedHoverBg: newstfUnlimitedHoverBg,
      oceanGreen: newstfTrueBlue,
      savingsTextColor: newstfPrimaryDefault,
      // header buttons
      headerCartBtn: newstfPrimaryDefault,
      headerCartBtnSmall: newstfPrimaryDefault,
      headerSignInBtnSmall: newstfSecondaryDefault,
      // links
      linkPrimaryColor: newstfNeutral1000,
      linkSecondaryColor: newstfPrimaryDefault,
      linkTertiaryColor: newstfSecondaryDefault,

      //specific colour definitions
      btnsPrimaryBg: newstfMidnight,
      btnsPrimaryColor: newstfWhite,
      btnsPrimaryHoverBg: newstfSecondaryBold,
      btnsPrimaryHoverColor: newstfWhite,
      btnsSecondaryBg: 'transparent',
      btnsSecondaryColor: newstfButtonTextDark,
      btnsSecondaryHoverBg: newstfButtonHoverRed,
      btnsSecondaryHoverColor: newstfWhite,
      btnsTertiaryBg: 'transparent',
      btnsTertiaryColor: newstfWhite,
      btnsTertiaryBorder: newstfWhite,
      btnsTertiaryHoverBg: newstfWhite,
      btnsTertiaryHoverColor: newstfButtonTextDark,
      pinkOfferPillBg: newstfPrimaryDefault,

      // Disabled button/link states used by shared button mixins
      grey00: newstfButtonDisabledBg,
      grey70: newstfButtonDisabledText,
      grey30: newstfButtonDisabledLink,

      footerPrimaryBg: newstfMidnight,
      footerdrinkaware: newstfWhite,
      surfaceWhite: newstfWhite,

      highlightMenu: newstfPrimaryDefault,

      bgColors: {
        LWPink: newstfPrimaryDefault,
        LWDarkBlue: newstfMidnight,
        LWCream: newstfNeutral100,
        LWPurple: newstfMidnight,
        LWSand: newstfTertiaryDefault,
        LWLightGreen: newstfSecondarySubtle,
        LWBlue: newstfTrueBlue,
        LWPeach: newstfPrimarySubtle,
        LWCTAGreen: newstfPrimaryDefault,
        LWDarkGreen: newstfPrimaryBold,
        whiteWine: newstfSecondarySubtle,
        redWine: newstfPrimaryDefault,
        roseWine: newstfPrimarySubtle,
        champagneAndSparkling: newstfMidnight,
        dessert: newstfPrimaryBold,
        fortifiedWine: newstfTertiarySubtle,
        wineBox: newstfNeutral400,
        lwlightError: newstfPrimarySubtle,
      },

      accentPrimarySubtle: newstfPrimarySubtle,
      accentPrimaryBold: newstfPrimaryBold,
      accentSecondarySubtle: newstfSecondarySubtle,
      accentSecondaryBold: newstfSecondaryBold,
      accentTertiarySubtle: newstfTertiarySubtle,
      accentTertiaryDefault: newstfTertiaryDefault,
      accentTertiaryBold: newstfTertiaryBold,
      neutral100: newstfNeutral100,
      neutral200: newstfNeutral200,
      neutral300: newstfNeutral300,
      neutral400: newstfNeutral400,
      neutral500: newstfNeutral500,
      neutral600: newstfNeutral600,
      neutral700: newstfNeutral700,
      neutral800: newstfNeutral800,
      neutral900: newstfNeutral900,
      neutral1000: newstfNeutral1000,
      whiteWine: newstfSecondarySubtle,
      cardBackground: newstfMidnight,
      headerSurface: newstfMidnight,
      headerPromoBg: newstfTrueBlue,
      // primary14 used in Cart member-price & member-savings overrides
      primary14: newstfUnlimitedBg,
      lwBodyBlack: newstfNeutral1000,
      lwGrey400: newstfNeutral400,
      unltPopupBackground: newstfMidnight,
      positiveDark: newstfSecondaryDefault,
      secondaryLight: newstfNeutral100,
      unlimitedButtonColor: newstfMidnight,
    },
    fonts: {
      familyPrimary: fontFamilies?.primary ?? `'IBM Plex Serif', serif`,
      familySecondary: fontFamilies?.secondary ?? `'Inter', sans-serif`,
      familyTertiary: fontFamilies?.tertiary ?? `'Inter', sans-serif`,
      headerMainNavTitle: fontFamilies?.secondary ?? `'Inter', sans-serif`,
      headerMainNavSubTitle: fontFamilies?.secondary ?? `'Inter', sans-serif`,
      weightLight: '400',
      weightNormal: '400',
      weightMedium: '500',
      weightSemiBold: '600',
      weightMediumPlus: '600',
      weightBold: '700',
      oswald: fontFamilies?.primary ?? `'IBM Plex Serif', serif`,
      roboto: fontFamilies?.secondary ?? `'Inter', sans-serif`,
      noto: fontFamilies?.tertiary ?? `'Inter', sans-serif`,
    },
  });
};

export const buildThemeMixinsAPACNewstf = (vars: Omit<Theme, 'mixins' | 'widgets'>) => {
  const buildThemeWidget = buildThemeMixinsAPACLW(vars);

  return merge({}, buildThemeWidget, {
    hxl: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize45};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight100};
      letter-spacing: ${vars.fonts.fontLetterSpacing1};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize64};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight100};
      }
    `,
    h1: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize34};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight105};
      letter-spacing: ${vars.fonts.fontLetterSpacing2};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize48};
        letter-spacing: ${vars.fonts.fontLetterSpacing2};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight105};
      }
    `,
    h2: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize30};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight105};
      letter-spacing: ${vars.fonts.fontLetterSpacing2};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize34};
        letter-spacing: ${vars.fonts.fontLetterSpacing2};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight105};
      }
    `,
    h3: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize28};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight110};
      letter-spacing: ${vars.fonts.fontLetterSpacing10};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize30};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight110};
      }
    `,
    h4: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize26};
      font-weight: ${vars.fonts.weightBold};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing4};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize30};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
    h5: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize24};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing4};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize26};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
    h6: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize20};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing4};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize22};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
    h7: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize16};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing6};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize18};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    h8: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize16};
      font-weight: ${vars.fonts.weightBold};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing6};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize18};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    eyebrowRegular: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize12};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing7};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize14};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    eyebrowSmall: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize10};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing7};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize12};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    utility: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize10};
      font-weight: ${vars.fonts.weightSemiBold};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing7};
      text-transform: ${vars.fonts.caseUpper};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize12};
        line-height: ${vars.fonts.fontLineHeight115};
        letter-spacing: ${vars.fonts.fontLetterSpacing8};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
    quote: `
      font-family: ${vars.fonts.familyPrimary};
      font-size: ${vars.fonts.fontSize20};
      font-style: ${vars.fonts.styleItalic};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing1};
      ${vars.breakpoints.sm} {
        font-size: ${vars.fonts.fontSize28};
      }
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
    body1: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize16};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight140};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      span {
        line-height: ${vars.fonts.fontLineHeight140};
      }
    `,
    body2: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize14};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight130};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      span {
        line-height: ${vars.fonts.fontLineHeight130};
      }
    `,
    bodySmall: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize12};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight130};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      span {
        line-height: ${vars.fonts.fontLineHeight130};
      }
    `,
    caption: `
      font-family: ${vars.fonts.familySecondary};
      font-size: ${vars.fonts.fontSize12};
      font-weight: ${vars.fonts.weightNormal};
      line-height: ${vars.fonts.fontLineHeight120};
      letter-spacing: ${vars.fonts.fontLetterSpacing5};
      span {
        line-height: ${vars.fonts.fontLineHeight120};
      }
    `,
  });
};

export const buildThemeWidgetStylesAPACNewstfFromBase = (
  vars: Omit<Theme, 'widgets'>,
  themeStyles: Record<string, any>,
): ReturnType<typeof buildThemeWidgetStylesAPACLW> => {
  const overrides: Record<string, any> = {};

  if (themeStyles.GlobalHeader) {
    overrides.GlobalHeader = {
      wrapper: `
      ${themeStyles.GlobalHeader?.wrapper ?? ''}
      &.mainHeader {
        background: ${(vars.colors as unknown as { [key: string]: string }).headerSurface};
        border-radius: 0;
        .sticky-header {
          background-color: ${(vars.colors as unknown as { [key: string]: string }).headerSurface};
          box-shadow: none;
          .search-navbar-container {
            background: ${(vars.colors as unknown as { [key: string]: string }).headerSurface};
          }
        }
      }
      &.mainHeader ~ .main-content-container {
        border-top: 1px solid ${vars.colors.white};
      }
      `,
      stripBanner: `
      ${themeStyles.GlobalHeader?.stripBanner ?? ''}
      a, p, span, div {
        color: ${vars.colors.white};
      }
      button {
        border-color: rgba(255, 255, 255, 0.45);
      }
      `,
      headerLogo: `
      img {
        max-width: 8em;
          ${vars.breakpoints.sm} {
            max-width: 13em;
          }
        &.drawer-header-logo-img {
          max-width: 13em;
        }
      }`,
      mainNavBarWrapper: `
        ${themeStyles.GlobalHeader?.mainNavBarWrapper ?? ''}
        .topLevelLink:hover .megaNavTab {
          color: ${vars.colors.white};
        }
        .megaNavTab {
          font-size: 18px;
          text-underline-offset: ${vars.space.lg};
          padding: ${vars.space.lg} 15px;
          display: inline-block;
          font-family: ${vars.fonts.headerMainNavTitle};
          color: ${vars.colors.white};
          letter-spacing: ${vars.fonts.fontLetterSpacing6};
          font-weight: 400;
          ${vars.breakpoints.xl} {
            padding: ${vars.space.lg} 20px;
          }

          &:hover {
            text-decoration: none;
          }

          &.emphasize, &.emphasize span {
            color: ${vars.colors.white};
            font-weight: ${vars.fonts.weightNormal};
          }
        }
        .header-main-nav-layout {
          .navigation-link .navigation-link a {
            color: ${vars.colors.linkPrimaryColor};
          }
          .navigation-link h4 {
            color: ${vars.colors.linkSecondaryColor};
          }
        }
      `,
      searchNavBar: `
        ${themeStyles.GlobalHeader?.searchNavBar ?? ''}
        ${vars.breakpoints.lg} {
          border-bottom: none;
        }
        .header-mainnav-mobile i {
          color: ${vars.colors.white};
        }
        .search-navbar-container {
          background: ${(vars.colors as unknown as { [key: string]: string }).headerSurface};
          .navbar-right-container {
            color: ${vars.colors.white};
            & > div {
              gap: 0;
            }
            a, p, span, svg {
              color: ${vars.colors.white};
              fill: ${vars.colors.white};
              font-size: ${vars.fonts.fontSize16};
            }
            .sign-in-link {
              color: ${vars.colors.white};
            }
          }
          .navbar-left-container {
            color: ${vars.colors.white};
            a, button, svg {
              color: ${vars.colors.white};
              fill: ${vars.colors.white};
            }
          }
          .navbar-center-container {
            color: ${vars.colors.white};
            .header-search-bar {
              .search-icon i,
              .isSearchBar i {
                color: ${vars.colors.white};
              }
            }
            .searchbar-grid-block-container .searchbar-grid,
            &.input-on-focus .searchbar-grid-block-container .searchbar-grid {
              background: ${(vars.colors as unknown as { [key: string]: string }).headerSurface};
            }
            .searchbar-cancel-container .searchbar-cancel-link {
              color: ${vars.colors.white};
            }
            .sticky-nav-bar {
              align-items: center;
              #header-logo {
                display: flex;
                justify-content: center;
                align-items: center;
              }
            }
          }
        }`,
      headerCart: `
        ${themeStyles.GlobalHeader?.headerCart ?? ''}
          .header-cart-link {
            .header-cart-icon-container i {
              color: ${vars.colors.white};
            }
          }

          .sticky-header & {
            .header-cart-link {
              .header-cart-icon-container i {
                color: ${vars.colors.white};
              }
            }
          }

          &.has-quantity {
            ${vars.breakpoints.lg} {
              .header-cart-link,
              div.header-cart-link {
                .sticky-header & {
                  .header-cart-price-container span,
                  .header-cart-icon-container i {
                    color: ${vars.colors.white} !important;
                  }
                }
              }
            }
          }
        `,
    };
  }

  if (themeStyles.Search) {
    overrides.Search = {
      hitWrapper: ` 
      ${themeStyles.Search?.hitWrapper ?? ''}
      &.grid-view {
        .ais-Hits-list {
          .ais-Hits-item {
            .hit-content {
              .title {
                font-family: ${vars.fonts.familyPrimary};
              }
            }
            .addToCart-container { 
              .price-section {
                .sku-price {
                  font-weight: ${vars.fonts.weightSemiBold};
                  font-family: ${vars.fonts.familyPrimary};
                  .original-price-bottle { 
                    font-family: ${vars.fonts.familyPrimary};
                  }
                  .vpp-price-section {
                    font-family: ${vars.fonts.familyPrimary};
                    .sales-price {
                      color: ${vars.colors.textPrimaryBg}
                    }
                  }
                }
              }
              .add-to-cart-btn-container {
                .top-price-section {
                  .price-section-sku {
                    .price {
                      font-family: ${vars.fonts.familyPrimary};
                    }
                  }
                  .price-section {
                    .pricing-panel-container {
                      .straight-sku-container {
                        .vpp-price {
                          color: ${vars.colors.textPrimaryBg};
                          &.first-price-head {
                            .price {
                              font-family: ${vars.fonts.familyPrimary};
                            }
                          }
                        }
                      }
                      .vpp-btn {
                        .member-price-section {
                          margin-top: 10px;
                        }
                      }
                    }
                  }
                }
                .loading-add-to-cart-btn:disabled {
                  background-color: ${vars.colors.btnsPrimaryBg};
                  color: ${vars.colors.btnsPrimaryColor};
                  opacity: 1;

                  .addingText {
                    color: ${vars.colors.btnsPrimaryColor};
                  }

                  [data-test='loading-spinner'] > div {
                    border-color: ${vars.colors.btnsPrimaryColor};
                    border-right-color: transparent;
                  }
                }
                button {
                  &:hover {
                    background-color: ${vars.colors.btnsPrimaryHoverBg}
                  }
                }
              }
            }
          }
        }
      }
      `,
    };
  }

  if (themeStyles.Product) {
    overrides.Product = {
      orderform: `
      ${themeStyles.Product?.orderform ?? ''}
      .straight-sku-container{
        .vpp-price {
            color: ${vars.colors.textPrimaryBg};
          }
        }

      .loading-add-to-cart-btn:disabled {
        background-color: ${vars.colors.btnsPrimaryBg};
        color: ${vars.colors.btnsPrimaryColor};
        opacity: 1;

        .addingText {
          color: ${vars.colors.btnsPrimaryColor};
        }

        [data-test='loading-spinner'] > div {
          border-color: ${vars.colors.btnsPrimaryColor};
          border-right-color: transparent;
        }
      }
      `,
    };
  }

  if (themeStyles.Footer) {
    overrides.Footer = {
      default: `
      ${themeStyles.Footer?.default ?? ''}
      font-family: ${vars.fonts.familySecondary};
      p {
        font-family: ${vars.fonts.familySecondary};
      }
      @media print {
        display: none;
      }
      .footer-nav-links {
        background: ${vars.colors.white};
        box-shadow: 0 0 0 100vmax ${vars.colors.white};
        color: ${vars.colors.black};
        .wrapper {
          p, a, span, li {
            color: ${vars.colors.black};
            font-size: ${vars.fonts.fontSize14};
          }
        }
      }
      .guidelines-policies {
        .wrapper p {
          font-size: ${vars.fonts.fontSize14};
        }
      }
      .social-connections {
        background: ${vars.colors.footerPrimaryBg};
        box-shadow: 0 0 0 100vmax ${vars.colors.footerPrimaryBg};
        clip-path: inset(0 -100vmax);
        color: ${vars.colors.white};
        .wrapper {
          p, a, span, svg {
            color: ${vars.colors.white};
            fill: ${vars.colors.white};
          }
        }
      }
      .footer-bottom-section {
        background: ${vars.colors.footerPrimaryBg};
        box-shadow: 0 0 0 100vmax ${vars.colors.footerPrimaryBg};
        clip-path: inset(0 -100vmax);
        color: ${vars.colors.white};
        .wrapper {
          .copyrights {
            p {
              color: ${vars.colors.white};
              a {
                color: ${vars.colors.white};
              }
            }
          }
          .brand img {
              max-width: 13em;
          }
          ${vars.breakpoints.lg} {
            .bedrinkaware-group {
              justify-content: flex-start;
            }
          }
        }
      }
      .social-connections {
        .wrapper {
          .social-connection-heading p {
            font-family: ${vars.fonts.familySecondary};
            letter-spacing: 2px;
            color: ${vars.colors.white};
          }
        }
      }
      `,
      navItems: `
        ${themeStyles.Footer?.navItems ?? ''}
        > div:first-of-type p{
          text-transform: uppercase;
          font-weight: bold;
        }
  `,
    };
  }

  if (themeStyles.FooterDropDown) {
    overrides.FooterDropDown = {
      main: `
        ${themeStyles.FooterDropDown?.main ?? ''}
        margin: 0;
        ${vars.breakpoints.sm} {
          margin: 0;
          padding: 15px 15%;
        }
        background: ${vars.colors.footerPrimaryBg};
        box-shadow: 0 0 0 100vmax ${vars.colors.footerPrimaryBg};
        clip-path: inset(0 -100vmax);
        color: ${vars.colors.white};
        .mantine-Accordion-item {
          border: none;
          border-bottom: 1px solid ${vars.colors.white};
          background-color: transparent;
          .mantine-Accordion-control:hover {
            background-color: transparent;
          }
          &[data-active] {
            background-color: transparent;
          }
          .mantine-Accordion-chevron {
            color: ${vars.colors.white};
          }
          .mantine-Accordion-label {
            font-weight: bold;
            font-size: ${vars.fonts.fontSize16};
            color: ${vars.colors.white};
            div, span {
              color: ${vars.colors.white};
            }
          }
          .mantine-Accordion-content {
            color: ${vars.colors.white};
            a {
              color: ${vars.colors.white};
            }
          }
        }
          `,
    };
  }

  if (themeStyles.DrawerMenu) {
    overrides.DrawerMenu = {
      mobileNavMenuContainer: `
        ${themeStyles.DrawerMenu?.mobileNavMenuContainer ?? ''}
        .mainnav-mobile-drawer-badges-panel {
          background-color: ${vars.colors.footerPrimaryBg};
        }
      `,
    };
  }

  if (themeStyles.Unlimited) {
    overrides.Unlimited = {
      btnPrimary: `
        background-color: ${vars.colors.textUnlimitedBg};
        &:hover, &:focus {
          background-color: ${vars.colors.textUnlimitedHoverBg};
        }
      `,
      memberPrice: `
        color: ${vars.colors.primary14};
      `,
    };
  }

  if (themeStyles.Cart) {
    overrides.Cart = {
      EDWrapper: `
        ${themeStyles.Cart?.EDWrapper ?? ''}
        .wrapper-container {
          button {
            font-size: 14px;
            font-weight: ${vars.fonts.weightMedium};
          }
        }
        .wrapper-container h2 , .order-total {
          font-weight: ${vars.fonts.weightMedium};
        }
        .quickOrder, .wrapper-container {
          background: #F8F8F8;
        }
        .gst-text {
          font-size: 16px;
        }
      `,
      quickOrder: `
        ${themeStyles.Cart?.quickOrder ?? ''}
        background: #F8F8F8;
        font-size: 16px;
        button {
          font-weight: ${vars.fonts.weightMedium};
        }
      `,
      MiniCart: `
      ${themeStyles.Cart?.MiniCart ?? ''}
      .mini-cart-header {
        h3 {
          text-transform: none;
        }
      }
      .product-great-pick {
        font-size: 22px;
        font-weight: 600;
      }
      .product-link {
        font-size: 22px;
        font-weight: 600;
      }
      .price-section .strike{
        font-style: italic;
        font-weight: ${vars.fonts.weightNormal};
      }
      .price-section .strike+.sale-price {
        color: ${vars.colors.textPrimaryBg};
        font-weight: ${vars.fonts.weightSemiBold};
      }
      .price-section .strike+.member-price {
        color: ${vars.colors.primary14};
      }
      .member-savings {
        color: ${vars.colors.primary14};
      }
    `,
      cartDetailSection: `
       ${themeStyles.Cart?.cartDetailSection ?? ''}
        .product-link {
          color: ${vars.colors.primary8};
          font-weight: ${vars.fonts.weightMedium};
        }`,
      orderSummaryContainer: `
        ${themeStyles.Cart?.orderSummaryContainer ?? ''}
        .continue-shopping span {
          font-weight: ${vars.fonts.weightMedium};
        }
        h2 {
          font-weight: ${vars.fonts.weightMedium};
          text-transform: none;
        }
        .order-summary-section {
          .order-summary-item {
            .order-summary-item-label {
              font-family: ${vars.fonts.familySecondary};
            }
          }
        }`,
      cartLayout: {
        cartCounterAndPrice: `
        ${themeStyles.Cart?.cartLayout?.cartCounterAndPrice ?? ''}
          .price-savings h6 {
            font-weight: ${vars.fonts.weightMedium};
          }
        `,
      },
    };
  }

  overrides.CmsTBSPage = cmsTBSStyles(vars);

  if (themeStyles.Membership) {
    overrides.Membership = {
      pageWrapper: `
      ${themeStyles.Membership?.pageWrapper ?? ''}
      & .sidebar{
        display: none;
        ${vars.breakpoints.md} {
           display:flex;
        }
        .grid-container div .navigation-link a.active {
          color: ${vars.colors.highlightMenu};
        }
      }
      & .top-section{
          .dynamicComponents{
            >.widget-container{
             .cms-image-container{
                background: ${vars.colors.whiteWine};
                display:flex;
                justify-content:center;
                img{
                  padding:10px;
                  width:300px;
                }
              }
            }
            >.widget-cms-column{
              & .grid-container {
                    justify-content: space-between;
                    & .cms-image-container img{
                      width:88px;

                    }

              }
            }
          }
        }
        & .bottom-section{
          & .accordion-item{
             padding: 15px 0px;
            .accordion-title h3{
                font-size: ${vars.fonts.fontSize18};
            }
            .accordion-content h8{
              font-weight: normal;

            }
          }
        }
        & .cancel-link-monthly-notes{
            ${vars.mixins.body1};
             background-color: ${vars.colors.secondaryLight};
             padding: 10px;
             margin-bottom:5px;
             i{
               margin-right:5px;
             }

        }
      `,
    };
  }

  if (themeStyles.MembershipRebranding) {
    overrides.MembershipRebranding = {
      membershipStatus: `
      ${themeStyles.MembershipRebranding?.membershipStatus ?? ''}
        border: 1px solid ${vars.colors.lwGrey400};
        padding: 24px;
        .re-join-note{
           margin-top: 12px;
        }
        .year-note {
          font-weight: ${vars.fonts.weightBold};
          font-family: ${vars.fonts.roboto};
          color: ${vars.colors.unltPopupBackground};
       
        }
        .add-unlimited-to-cart button{
           margin-top: 20px;
          background: ${vars.colors.unltPopupBackground};
          :hover{
           background-color: ${(vars.colors.bgColors as unknown as { [key: string]: string }).LWDarkBlue};
           color: ${vars.colors.white};
            
          }
        }
        .saving-instruction {
          font-family: ${vars.fonts.roboto};
          padding-top: 10px;
          font-weight: ${vars.fonts.weightNormal};
          font-size: ${vars.fonts.sizeLarge};
        }
        .saving-note {
          font-family: ${vars.fonts.roboto};
          font-weight: ${vars.fonts.weightNormal};
          font-size: ${vars.fonts.fontSize14};
          color: ${vars.colors.grey60};
          margin-top: 12px;
        }
        .price-section {
          display: flex;
          align-items: baseline;
        }
        .membership-status-title {
          font-family: ${vars.fonts.noto};
          font-weight: ${vars.fonts.weightMediumPlus};
        }
        .membership-subscription-title {
          font-family: ${vars.fonts.roboto};
          font-weight: 700;
          font-size: ${vars.fonts.sizeLarge};
          padding-top: 10px;
          .monthly-notes{
            ${vars.mixins.body2};
            i{
              margin:0px 10px;
            }
          }
        }
        .enroll-info {
          margin-top: 12px;
          font-family: ${vars.fonts.roboto};
          font-weight: ${vars.fonts.weightBold};
          font-size: ${vars.fonts.sizeLarge};
          & .date {
            font-weight: ${vars.fonts.weightNormal};
            font-size: ${vars.fonts.sizeLarge};
            padding-right: 8px;
          }
        }
      `,
      layout: `
         ${themeStyles.MembershipRebranding?.layout ?? ''}
         & .change-payment-card a{
           font-family: ${vars.fonts.roboto};
           text-decoration: underline;
           font-size: ${vars.fonts.fontSize14};
           }
       `,
    };
  }

  if (themeStyles.MyAccount) {
    overrides.MyAccount = {
      unlimitedModalWrapper: `
       ${themeStyles.MyAccount?.unlimitedModalWrapper ?? ''}
        & .mantine-Modal-title{
          font-weight: ${vars.fonts.weightMedium};
        }
       .unlimited-modal-content-wrapper{
          gap:20px;
         
          .mantine-Text-root{
              font-family: ${vars.fonts.roboto};
              font-size: ${vars.fonts.fontSize16};
              font-weight: ${vars.fonts.weightNormal};
          }
          .swap-note{
             ${vars.mixins.body1};
            
          }
          .heading-notes{
            font-size: ${vars.fonts.fontSize16};
            }
          }
         .unlimited-icons{
           i {
            color:${vars.colors.primary14} !important;
            font-size: 50px;
            background: ${vars.colors.whiteWine};
            border-radius: 100%;
           }
           .notes .notes-heading{
             ${vars.mixins.eyebrowRegular};
             ${vars.breakpoints.sm} 
             {
              font-size: ${vars.fonts.fontSize12};
             }

            }
         }
         button.unlimited-modal-tertiary-cta {
          font-weight: ${vars.fonts.weightSemiBold};
         }
         & .monthly-offer-box{
          .radio-offer{
             border: 1px solid ${vars.colors.lwGrey400};
             margin:15px;
             padding:10px;
            &.active{
               border: 1.5px solid ${vars.colors.lwBodyBlack};
            }
            & input:checked:not(:disabled) {
                background-color: transparent;
                border: 2px solid ${vars.colors.btnsPrimaryBg};
                ~ svg{
                  color: ${vars.colors.btnsPrimaryBg};
                }
              }   
          
            label{
                ${vars.mixins.body1};
                 font-weight: ${vars.fonts.weightBold};
            }
            .badge-row {
              display: flex;
               justify-content: space-between;
              .mantine-Badge-root{
                ${vars.mixins.body3};
                    font-weight: ${vars.fonts.weightMediumPlus};
                background-color: ${vars.colors.positiveDark};
                color:${vars.colors.white};
                text-transform: capitalize;
                padding:10px;
               }
              }
              .offer-notes{
                margin-left: 5%;
                ${vars.mixins.body2};
                margin-top: 12px;
              }
          }
         }
    `,
    };
  }

  return merge({}, themeStyles, overrides) as ReturnType<typeof buildThemeWidgetStylesAPACLW>;
};

export const buildThemeWidgetStylesAPACNewstf = (vars: Omit<Theme, 'widgets'>) =>
  buildThemeWidgetStylesAPACNewstfFromBase(vars, buildThemeWidgetStylesAPACLW(vars));

export { buildThemeWidgetStylesAPACLWFromBase } from 'dw-apac-law/theme-foundation';

export {
  buildCoreWidgets,
  buildProductCardWidgets,
  buildProductWidgets,
  buildSearchWidgets,
  buildMiniCartWidgets,
  buildCartWidgets,
  buildAccountWidgets,
  buildLandingpageWidgets,
} from 'dw-apac-law/theme-foundation';
